import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { JobPost } from '~/api/jobPosts/types';
import type { IconName } from '~/components';

import { JobPostLocationType } from '~/api/jobPosts/constants';
import { useIntl } from '~/hooks/useIntl';

type JobPostSpecType = {
  icon: IconName;
  label: string;
  value: string | null;
};

export type JobPostSpecKey = 'closesAt' | 'experienceLevel' | 'location' | 'locationType' | 'period' | 'price' | 'region';

type JobPostSpecsType = Record<JobPostSpecKey, JobPostSpecType>;

export const useJobPostSpecs = (jobPost: JobPost): JobPostSpecsType => {
  const { t } = useTranslation(['community', 'jobPosts']);
  const { formatCurrency } = useIntl();

  return useMemo(
    () => ({
      closesAt: {
        label: t('jobPosts:specifications.closesAt'),
        value: jobPost.closesAt ? dayjs(jobPost.closesAt).format('DD/MM/YYYY') : null,
        icon: 'HourglassBottom',
      },
      experienceLevel: {
        label: t('jobPosts:specifications.experienceLevel'),
        value: jobPost.experienceLevel !== null ? t(`jobPosts:fields.experienceLevel.options.${jobPost.experienceLevel}`) : null,
        icon: 'Level',
      },
      location: {
        label: t('jobPosts:specifications.location'),
        value:
          jobPost.locationType === JobPostLocationType.Remote
            ? t('jobPosts:fields.locationType.options.Remote')
            : jobPost.region !== null
              ? t(`community:regions.${jobPost.region}`)
              : null,
        icon: 'Location',
      },
      locationType: {
        label: t('jobPosts:specifications.locationType'),
        value: jobPost.locationType !== null ? t(`jobPosts:fields.locationType.options.${jobPost.locationType}`) : null,
        icon: 'Location',
      },
      period: {
        label: t('jobPosts:specifications.period'),
        value: (() => {
          const startsAt = jobPost.startsAt ? dayjs(jobPost.startsAt).format('DD/MM/YYYY') : null;
          const endsAt = jobPost.endsAt ? dayjs(jobPost.endsAt).format('DD/MM/YYYY') : null;

          return [startsAt, endsAt].filter(Boolean).join(' - ') || null;
        })(),
        icon: 'HourglassTop',
      },
      price: {
        label: t('jobPosts:specifications.price'),
        value: (() => {
          if (jobPost.priceType === 'Tbd' || jobPost.price === null) {
            return t('jobPosts:fields.isPriceToBeDiscussed.label');
          }

          const price = formatCurrency(jobPost.price);
          if (jobPost.priceType === 'Fixed') {
            return price;
          }

          const unit = t(`jobPosts:fields.priceUnit.options.${jobPost.priceType}`);

          return `${price} / ${unit}`;
        })(),
        icon: 'Payments',
      },
      region: {
        label: t('jobPosts:specifications.location'),
        value: jobPost.region !== null ? t(`community:regions.${jobPost.region}`) : null,
        icon: 'Location',
      },
    }),
    [formatCurrency, jobPost, t],
  );
};
